import React, { memo } from 'react';
import { FixedSizeList } from 'react-window';

/**
 * Virtual scrolling list component for calls
 * Only renders visible items, handles 50,000+ records efficiently
 */
const VirtualCallList = ({
  calls = [],
  onCallClick,
  height = 600,
  itemHeight = 120,
  hasMore = false,
  loading = false,
  onLoadMore = null,
  observerTarget = null
}) => {
  // Memoized call item component
  const CallItem = memo(({ index, style, data }) => {
    const call = data.calls[index];
    if (!call) return null;

    return (
      <div style={style}>
        <div
          onClick={() => data.onClick(call)}
          className="p-3 mx-2 mb-2 rounded-lg border hover:bg-gray-50 cursor-pointer transition-colors"
        >
          <div className="flex justify-between items-start mb-2">
            <div className="flex-1">
              <p className="font-medium text-sm">
                {call.username || call.to || call.phone || 'Unknown Number'}
              </p>
              <p className="text-xs text-gray-500">
                {call.to && `To: ${call.to}`}
              </p>
            </div>
            <div className="flex flex-col items-end space-y-1">
              <span className={`px-2 py-1 text-xs rounded-full ${
                call.status === 'completed' ? 'bg-green-100 text-green-800' :
                call.status === 'failed' ? 'bg-red-100 text-red-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {call.status || 'completed'}
              </span>
              {call.callSid && (
                <span className="text-xs text-gray-400 font-mono">
                  {call.callSid.substring(0, 8)}...
                </span>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {new Date(call.createdAt || call.created_at).toLocaleString('en-US')}
          </p>
          {call.transcript && call.transcript.length > 0 && (
            <p className="text-xs text-gray-600 mt-1">
              Messages: {call.transcript.length}
            </p>
          )}
          {call.summary && (
            <p className="text-xs text-gray-600 mt-1 truncate" title={call.summary}>
              Summary: {call.summary}
            </p>
          )}
        </div>
      </div>
    );
  });

  CallItem.displayName = 'CallItem';

  if (calls.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        No call records found
      </div>
    );
  }

  return (
    <div className="relative">
      <FixedSizeList
        height={height}
        itemCount={calls.length}
        itemSize={itemHeight}
        width="100%"
        itemData={{
          calls,
          onClick: onCallClick
        }}
        overscanCount={5} // Render 5 extra items above/below viewport for smooth scrolling
      >
        {CallItem}
      </FixedSizeList>
      
      {/* Loading indicator and observer target for infinite scroll */}
      {(hasMore || loading) && (
        <div 
          ref={observerTarget} 
          className="h-10 flex items-center justify-center py-4"
        >
          {loading && <div className="text-gray-500">Loading more calls...</div>}
        </div>
      )}
    </div>
  );
};

export default memo(VirtualCallList);

